import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AnimatedSection from '../components/AnimatedSection';
import './Resources.css';

const Resources = () => {
  const resourceCategories = [
    {
      icon: 'bi-hospital-fill',
      title: 'Healthcare Resources',
      color: 'bg-primary-teal',
      resources: [
        { name: 'Community Health Clinics', description: 'Affordable healthcare services for all' },
        { name: 'Mental Health Support', description: 'Counseling and therapy resources' },
        { name: 'Prescription Assistance', description: 'Help with medication costs' },
        { name: 'Health Insurance Enrollment', description: 'Navigate insurance options' },
      ],
    },
    {
      icon: 'bi-house-heart-fill',
      title: 'Housing Assistance',
      color: 'bg-primary-orange',
      resources: [
        { name: 'Affordable Housing Programs', description: 'Low-income housing options' },
        { name: 'Rental Assistance', description: 'Help paying rent and utilities' },
        { name: 'Emergency Shelter Services', description: 'Immediate housing support' },
        { name: 'Homeownership Programs', description: 'First-time buyer resources' },
      ],
    },
    {
      icon: 'bi-book-fill',
      title: 'Education & Training',
      color: 'bg-primary-purple',
      resources: [
        { name: 'Adult Education Programs', description: 'GED and ESL classes' },
        { name: 'Vocational Training', description: 'Learn new job skills' },
        { name: 'Scholarship Opportunities', description: 'Financial aid for education' },
        { name: 'Online Learning Platforms', description: 'Free courses and certifications' },
      ],
    },
    {
      icon: 'bi-cart-fill',
      title: 'Food & Nutrition',
      color: 'bg-primary-teal',
      resources: [
        { name: 'Food Banks & Pantries', description: 'Free food assistance' },
        { name: 'SNAP Benefits', description: 'Supplemental nutrition assistance' },
        { name: 'Meal Programs', description: 'Community meals and delivery' },
        { name: 'Nutrition Education', description: 'Healthy eating guidance' },
      ],
    },
    {
      icon: 'bi-shield-slash-fill',
      title: 'Legal Support',
      color: 'bg-primary-orange',
      resources: [
        { name: 'Legal Aid Services', description: 'Free legal consultation' },
        { name: 'Immigration Assistance', description: 'Help with immigration matters' },
        { name: 'Family Law Resources', description: 'Divorce, custody, and support' },
        { name: 'Consumer Protection', description: 'Rights and advocacy support' },
      ],
    },
    {
      icon: 'bi-cash-coin',
      title: 'Financial Resources',
      color: 'bg-primary-purple',
      resources: [
        { name: 'Credit Counseling', description: 'Debt management and repair' },
        { name: 'Financial Literacy Workshops', description: 'Money management skills' },
        { name: 'Emergency Financial Aid', description: 'Short-term financial help' },
        { name: 'Banking Services', description: 'Low-cost banking options' },
      ],
    },
  ];

  const tools = [
    {
      icon: 'bi-calculator',
      title: 'Budget Calculator',
      description: 'Plan your monthly budget and track expenses',
      color: 'text-primary-teal',
    },
    {
      icon: 'bi-file-earmark-text',
      title: 'Resume Builder',
      description: 'Create a professional resume in minutes',
      color: 'text-primary-orange',
    },
    {
      icon: 'bi-calendar-check',
      title: 'Appointment Scheduler',
      description: 'Book consultations and services easily',
      color: 'text-primary-purple',
    },
    {
      icon: 'bi-journal-text',
      title: 'Resource Directory',
      description: 'Searchable database of community resources',
      color: 'text-primary-teal',
    },
  ];

  const helpfulLinks = [
    { category: 'Government Resources', links: ['Benefits.gov', 'USA.gov', 'IRS.gov'] },
    { category: 'Employment', links: ['Job Corps', 'CareerOneStop', 'Indeed'] },
    { category: 'Health', links: ['Healthcare.gov', 'SAMHSA', 'CDC'] },
    { category: 'Education', links: ['FAFSA', 'Khan Academy', 'Coursera'] },
  ];

  return (
    <div className="resources-page mt-5">
      {/* Hero Section */}
      <section className="c-resources-hero position-relative overflow-hidden py-5 py-lg-6" style={{background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.08) 0%, rgba(255, 255, 255, 1) 100%)'}}>
        <div className="position-absolute" style={{top: '10%', right: '5%', width: '400px', height: '400px', backgroundImage: 'radial-gradient(circle, rgba(41, 158, 129, 0.06) 0%, transparent 70%)', borderRadius: '50%'}}></div>
        <div className="container position-relative">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <AnimatedSection className="text-center">
                <div className="c-hero-badge bg-primary-orange bg-opacity-10 text-primary-orange d-inline-flex align-items-center px-4 py-2 rounded-pill mb-4 shadow-sm">
                  <i className="bi bi-book-fill me-2 fs-5"></i>
                  <span className="fw-bold">Resource Library</span>
                </div>
                <h1 className="display-3 fw-bold mb-4 text-dark" style={{letterSpacing: '-1px'}}>
                  Community Resources
                </h1>
                <p className="fs-4 text-muted lh-lg mb-5 mx-auto" style={{maxWidth: '750px'}}>
                  Access a comprehensive collection of resources designed to support you and your family.
                  We've compiled essential services, tools, and information to help you thrive.
                </p>
                <div className="c-resource-stats row g-4 justify-content-center mx-auto" style={{maxWidth: '800px'}}>
                  <div className="col-6 col-md-3">
                    <motion.div
                      className="c-stat-box bg-white rounded-4 shadow-sm p-4 border border-1 border-light"
                      whileHover={{ scale: 1.05 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <div className="c-stat-icon bg-primary-teal bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-2" style={{width: '50px', height: '50px'}}>
                        <i className="bi bi-collection-fill text-primary-teal fs-5"></i>
                      </div>
                      <h3 className="h4 fw-bold text-primary-teal mb-1">100+</h3>
                      <p className="small text-muted mb-0">Resources</p>
                    </motion.div>
                  </div>
                  <div className="col-6 col-md-3">
                    <motion.div
                      className="c-stat-box bg-white rounded-4 shadow-sm p-4 border border-1 border-light"
                      whileHover={{ scale: 1.05 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <div className="c-stat-icon bg-primary-orange bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-2" style={{width: '50px', height: '50px'}}>
                        <i className="bi bi-folder-fill text-primary-orange fs-5"></i>
                      </div>
                      <h3 className="h4 fw-bold text-primary-orange mb-1">6</h3>
                      <p className="small text-muted mb-0">Categories</p>
                    </motion.div>
                  </div>
                  <div className="col-6 col-md-3">
                    <motion.div
                      className="c-stat-box bg-white rounded-4 shadow-sm p-4 border border-1 border-light"
                      whileHover={{ scale: 1.05 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <div className="c-stat-icon bg-primary-purple bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-2" style={{width: '50px', height: '50px'}}>
                        <i className="bi bi-tools text-primary-purple fs-5"></i>
                      </div>
                      <h3 className="h4 fw-bold text-primary-purple mb-1">24/7</h3>
                      <p className="small text-muted mb-0">Access</p>
                    </motion.div>
                  </div>
                  <div className="col-6 col-md-3">
                    <motion.div
                      className="c-stat-box bg-white rounded-4 shadow-sm p-4 border border-1 border-light"
                      whileHover={{ scale: 1.05 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <div className="c-stat-icon bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-2" style={{width: '50px', height: '50px'}}>
                        <i className="bi bi-check-circle-fill text-success fs-5"></i>
                      </div>
                      <h3 className="h4 fw-bold text-success mb-1">Free</h3>
                      <p className="small text-muted mb-0">Always</p>
                    </motion.div>
                  </div>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Resource Categories */}
      <section className="resource-categories section-padding">
        <div className="container">
          <AnimatedSection className="section-title">
            <h2>Browse Resources by Category</h2>
            <p>Find the support you need across various areas of life</p>
          </AnimatedSection>

          <div className="row g-4">
            {resourceCategories.map((category, index) => (
              <AnimatedSection className="col-lg-6" key={index} delay={index * 0.1}>
                <motion.div
                  className="resource-category-card"
                  whileHover={{ y: -5 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <div className="category-header">
                    <div className={`category-icon ${category.color}`}>
                      <i className={category.icon}></i>
                    </div>
                    <h3>{category.title}</h3>
                  </div>
                  <div className="category-resources">
                    {category.resources.map((resource, idx) => (
                      <div className="resource-item" key={idx}>
                        <div className="resource-bullet"></div>
                        <div>
                          <h6>{resource.name}</h6>
                          <p>{resource.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Link to="/contact" className="resource-cta">
                    Get Connected <i className="bi bi-arrow-right"></i>
                  </Link>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Tools Section */}
      <section className="tools-section section-padding bg-gradient-section">
        <div className="container">
          <AnimatedSection className="section-title text-center">
            <h2 className="text-white">Helpful Tools</h2>
            <p className="text-white-50">Free tools to help you plan and succeed</p>
          </AnimatedSection>

          <div className="row g-4">
            {tools.map((tool, index) => (
              <AnimatedSection className="col-lg-3 col-md-6" key={index} delay={index * 0.1}>
                <motion.div
                  className="tool-card"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <i className={`${tool.icon} ${tool.color}`}></i>
                  <h5>{tool.title}</h5>
                  <p>{tool.description}</p>
                  <button className="btn btn-sm btn-light">
                    Coming Soon
                  </button>
                </motion.div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Helpful Links */}
      <section className="links-section section-padding">
        <div className="container">
          <AnimatedSection className="section-title">
            <h2>Helpful External Links</h2>
            <p>Trusted resources from reputable organizations</p>
          </AnimatedSection>

          <div className="row g-4">
            {helpfulLinks.map((linkGroup, index) => (
              <AnimatedSection className="col-lg-3 col-md-6" key={index} delay={index * 0.1}>
                <div className="link-card">
                  <h5>{linkGroup.category}</h5>
                  <ul>
                    {linkGroup.links.map((link, idx) => (
                      <li key={idx}>
                        <a href="https://www.google.com" target="_blank" rel="noopener noreferrer">
                          <i className="bi bi-box-arrow-up-right"></i>
                          {link}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Resources */}
      <section className="emergency-section section-padding bg-primary-light">
        <div className="container">
          <AnimatedSection className="text-center">
            <div className="emergency-card">
              <i className="bi bi-exclamation-triangle-fill text-warning"></i>
              <h2>Need Immediate Assistance?</h2>
              <p className="lead mb-4">
                If you're facing a crisis or emergency, here are important numbers to call:
              </p>
              <div className="emergency-numbers">
                <div className="emergency-number">
                  <h4>911</h4>
                  <p>Emergency Services</p>
                </div>
                <div className="emergency-number">
                  <h4>988</h4>
                  <p>Suicide & Crisis Lifeline</p>
                </div>
                <div className="emergency-number">
                  <h4>211</h4>
                  <p>Community Resources</p>
                </div>
                <div className="emergency-number">
                  <h4>952-486-3817</h4>
                  <p>MWC Connections</p>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section section-padding">
        <div className="container">
          <AnimatedSection className="text-center">
            <h2 className="mb-4">Can't Find What You're Looking For?</h2>
            <p className="lead mb-4">
              Our team is here to help you find the right resources. Contact us today.
            </p>
            <Link to="/contact" className="btn btn-primary-teal btn-lg">
              Contact Us for Help
              <i className="bi bi-arrow-right ms-2"></i>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Resources;
